import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, EmptyState } from "@/components/admin/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TEAM_ROLES } from "@/lib/constants";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Team() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", role: "Sales Executive" });

  const load = async () => setMembers((await api.get("/team")).data);
  useEffect(() => { load(); }, []);

  const create = async () => { await api.post("/team", form); toast.success("Member added"); setOpen(false); setForm({ name: "", mobile: "", role: "Sales Executive" }); load(); };
  const remove = async (id) => { await api.delete(`/team/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <PageHeader title="Team Performance" subtitle={`${members.length} team members`}>
        <Button onClick={() => setOpen(true)} data-testid="add-member-btn" className="h-10 rounded-xl" style={{ background: "linear-gradient(135deg,#FFA14D,#E85D1F)", color: "#ffffff" }}><Plus size={16} /> Add Member</Button>
      </PageHeader>

      <Panel>
        {members.length === 0 ? <EmptyState text="No team members" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-3 px-2">Member</th><th className="px-2">Role</th><th className="px-2 text-center">Assigned</th><th className="px-2 text-center">Contacted</th><th className="px-2 text-center">Follow-ups</th><th className="px-2 text-center">Meetings</th><th className="px-2 text-center">Conversions</th><th className="px-2 text-center">Rate</th><th className="px-2"></th>
              </tr></thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.member_id} data-testid="team-row" className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 px-2 font-semibold text-[#081226]">{m.name}<p className="font-mono-num text-xs text-slate-400">{m.mobile}</p></td>
                    <td className="px-2"><span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{m.role}</span></td>
                    <td className="px-2 text-center font-mono-num">{m.leads_assigned}</td>
                    <td className="px-2 text-center font-mono-num">{m.leads_contacted}</td>
                    <td className="px-2 text-center font-mono-num">{m.followups}</td>
                    <td className="px-2 text-center font-mono-num">{m.trials}</td>
                    <td className="px-2 text-center font-mono-num font-bold text-emerald-700">{m.conversions}</td>
                    <td className="px-2 text-center font-mono-num font-bold text-[#B8860B]">{m.conv_rate}%</td>
                    <td className="px-2 text-right"><button onClick={() => remove(m.member_id)} className="text-slate-300 hover:text-rose-500" data-testid="team-delete-btn"><Trash2 size={16} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-semibold">Name</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name-input" /></div>
            <div><label className="text-sm font-semibold">Mobile</label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="member-mobile-input" /></div>
            <div><label className="text-sm font-semibold">Role</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TEAM_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={create} disabled={!form.name} data-testid="member-save-btn" className="btn-orange">Add Member</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
